import type { ComponentProps } from 'react';
import { useEffect, useRef, useState } from 'react';

import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '../lib/utils';

import { Input } from './input';

function highlightMatch(text: string, search: string) {
  if (!search) return text;
  const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearch})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <strong key={i}>{part}</strong>
    ) : (
      <span key={`text-${i}`}>{part}</span>
    ),
  );
}

export function Autocomplete({
  options,
  value,
  onChange,
  search,
  onInputChange,
  className,
  debounceMs = 300,
  noOptionsText = 'No option found.',
  popoverClassName,
  onClear,
  disabled,
  ...inputProps
}: {
  options: { value: string; label: string; secondaryLabel?: string }[];
  value: string;
  onChange: (value: string) => void;
  search: string;
  onInputChange: (value: string) => void;
  debounceMs?: number;
  noOptionsText?: string;
  popoverClassName?: string;
  onClear?: () => void;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange'>) {
  const [isOpen, setIsOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState(search);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="vai-relative">
      <div className="vai-relative">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-z-10"
        />
        <Input
          ref={inputRef}
          placeholder="Search option..."
          value={localSearch}
          onChange={(e) => {
            const targetValue = e.target.value;
            setLocalSearch(targetValue);
            if (debounceTimeout.current) {
              clearTimeout(debounceTimeout.current);
            }
            debounceTimeout.current = setTimeout(() => {
              onInputChange(targetValue);
            }, debounceMs);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className={cn('vai-w-full vai-pl-10 vai-pr-8', className)}
          disabled={disabled}
          {...inputProps}
        />
        {onClear && (
          <FontAwesomeIcon
            icon={faXmark}
            onClick={onClear}
            className={cn(
              'vai-absolute vai-right-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-z-10',
              {
                'vai-cursor-pointer': !disabled,
                'vai-cursor-not-allowed vai-opacity-50 vai-pointer-events-none':
                  disabled,
              },
            )}
          />
        )}
      </div>
      {isOpen && (
        <div
          className={cn(
            'vai-absolute vai-top-full vai-w-full vai-z-50 vai-mt-1 vai-rounded-md vai-border vai-bg-white vai-shadow-lg',
            popoverClassName,
          )}
        >
          <div className="vai-py-1">
            {options.length === 0 ? (
              <div className="vai-px-2 vai-py-1.5 vai-text-sm vai-text-gray-500">
                {noOptionsText}
              </div>
            ) : (
              options.map((option) => (
                <button
                  key={option.value}
                  className="vai-relative vai-flex vai-w-full vai-cursor-pointer vai-select-none vai-flex-col vai-items-start vai-rounded-sm vai-px-2 vai-py-1.5 vai-text-sm vai-outline-none hover:vai-bg-gray-100 focus:vai-bg-gray-100"
                  onClick={() => {
                    onChange(option.value === value ? '' : option.value);
                    onInputChange(option.label);
                    setIsOpen(false);
                  }}
                >
                  <div>{highlightMatch(option.label, localSearch)}</div>
                  {option.secondaryLabel && (
                    <div className="vai-text-xs vai-text-gray-500 vai-mt-0.5">
                      {option.secondaryLabel}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
